import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

/*
 * URL state of the admin pages (filters, sort, tabs, the open link panel).
 *
 * react-router's setSearchParams(fn) hands the updater the params of the render
 * the handler came from, not the URL as it is now: two changes in quick
 * succession (a typed search and a select, two chips in one frame) build on the
 * same old params and the second undoes the first. Every admin page goes
 * through this helper instead: an update starts from window.location.search,
 * which the router has already replaced synchronously for the previous one.
 */

/** A patch of the query: a string sets the key, '' / null / undefined deletes it. */
export type ParamPatch = Record<string, string | null | undefined>

export interface UrlParams {
  params: URLSearchParams
  /** Apply a patch to the URL as it is now; replace (default) keeps the history clean. */
  update: (patch: ParamPatch, opts?: { replace?: boolean }) => void
}

/** Apply a patch to a query string (pure; exported for tests and links). */
export function patchQuery(search: string, patch: ParamPatch): URLSearchParams {
  const q = new URLSearchParams(search)
  for (const [k, v] of Object.entries(patch)) {
    if (v) q.set(k, v)
    else q.delete(k)
  }
  return q
}

export function useUrlParams(): UrlParams {
  const [params, setParams] = useSearchParams()
  const update = useCallback(
    (patch: ParamPatch, opts: { replace?: boolean } = {}) => {
      setParams(() => patchQuery(window.location.search, patch), { replace: opts.replace ?? true })
    },
    [setParams],
  )
  return { params, update }
}
